import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Footer from "./Footer";
import CustomButton from "../component/elements/CustomButton/CustomButton";
import axiosInstance from "../configuration/request";
import { useSnackbar } from "../component/SnackbarContext";
import { MdWarningAmber } from "react-icons/md";

export default function DeleteAccount() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const showSnackbar = useSnackbar();
  const { loginDetails } = useSelector((state) => state.counter);
  const isLoggedIn = !!loginDetails?.jwt && !!loginDetails?.userId;

  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const displayName = loginDetails?.name || "your account";
  const displayEmail = loginDetails?.email || "";
  const canSubmit =
    isLoggedIn &&
    acknowledged &&
    confirmText.trim().toUpperCase() === "DELETE" &&
    !deleting;

  const handleDeleteAccount = async () => {
    if (!canSubmit) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/customer/users/${loginDetails.userId}`);
      showSnackbar("Your account has been deleted.", "success");
      dispatch({ type: "RESET_STORE" });
      navigate("/", { replace: true });
    } catch (error) {
      showSnackbar(
        error?.response?.data?.message ||
          "We could not delete your account. Please contact support@tydii.io.",
        "error"
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="font-poppins h-[calc(100vh-80px)] mt-[80px] overflow-y-auto scrollbar-hide">
      <div
        className="w-full py-12 md:py-16 flex justify-center items-center"
        style={{
          background:
            "linear-gradient(90deg, #02537F 0%, #0585CB 60%, #035885 100%)",
        }}
      >
        <div className="mx-4 md:mx-6 lg:mx-14 text-center max-w-3xl">
          <h1 className="text-white font-bold text-3xl md:text-4xl lg:text-5xl leading-tight mb-3">
            Delete your account
          </h1>
          <p className="text-white/90 text-base md:text-lg">
            Permanently remove your Tydii account and associated profile data.
          </p>
        </div>
      </div>

      <div className="mx-4 md:mx-6 lg:mx-14 py-10 md:py-14">
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-10">
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <MdWarningAmber className="text-2xl shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">This action cannot be undone.</p>
              <p className="mt-1 text-amber-900/90">
                Deleting your account removes access to orders, saved addresses,
                favorites, and partner profiles linked to this login. Some
                records may be retained where required by law or for completed
                transactions.
              </p>
            </div>
          </div>

          {!isLoggedIn ? (
            <div className="mt-8 space-y-4 text-sm text-gray-700">
              <p>
                Sign in with the account you want to delete, then return to this
                page to complete removal.
              </p>
              <div className="flex flex-wrap gap-3">
                <CustomButton
                  onClick={() =>
                    navigate("/auth/account", {
                      state: { redirectTo: "/delete-account" },
                    })
                  }
                  customeClass="gradient-button h-[42px] px-6"
                >
                  Sign in to continue
                </CustomButton>
                <CustomButton
                  onClick={() => navigate("/")}
                  customeClass="primary-custom-button h-[42px] px-6"
                >
                  Back to home
                </CustomButton>
              </div>
              <p className="text-xs text-gray-500 pt-2">
                Need help? Email{" "}
                <a
                  href="mailto:support@tydii.io"
                  className="text-[#02537F] hover:underline"
                >
                  support@tydii.io
                </a>
              </p>
            </div>
          ) : (
            <div className="mt-8 space-y-5 text-sm text-gray-700">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
                  Signed in as
                </p>
                <p className="font-semibold text-gray-900 mt-1">{displayName}</p>
                {displayEmail ? (
                  <p className="text-gray-600 break-all">{displayEmail}</p>
                ) : null}
              </div>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="mt-1 accent-[#02537F]"
                />
                <span>
                  I understand that deleting my account is permanent and I will
                  lose access to my Tydii data and services.
                </span>
              </label>

              <div>
                <label
                  htmlFor="delete-confirm"
                  className="block text-sm font-medium text-gray-800 mb-1"
                >
                  Type <span className="font-mono font-semibold">DELETE</span>{" "}
                  to confirm
                </label>
                <input
                  id="delete-confirm"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3383B2]/40 focus:border-[#3383B2]"
                  autoComplete="off"
                />
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <CustomButton
                  onClick={handleDeleteAccount}
                  disabled={!canSubmit}
                  customeClass="h-[42px] px-6 min-w-[180px] !bg-red-600 hover:!bg-red-700 !text-white rounded-lg disabled:opacity-50"
                >
                  {deleting ? "Deleting…" : "Delete my account"}
                </CustomButton>
                <CustomButton
                  onClick={() => navigate(-1)}
                  disabled={deleting}
                  customeClass="primary-custom-button h-[42px] px-6"
                >
                  Cancel
                </CustomButton>
              </div>
            </div>
          )}
        </div>

        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="inline-flex items-center justify-center rounded-lg text-white bg-[#02537F] hover:bg-sky-800 px-6 py-2.5 text-base font-bold shadow-sm transition"
          >
            Back to Home
          </button>
        </div>
      </div>

      <div className="bg-[#034F78]">
        <Footer />
      </div>
    </div>
  );
}
